import { addDays, daysInMonth, monthPeriod, todayIso, toIso, yearPeriod } from '@apka/core'
import type { IsoDate } from '@apka/core'
import { useMemo, useState } from 'react'
import { useOdhaczanie } from '../dane/odhaczanie.js'
import type { Platnosc } from '../dane/typy.js'
import { uzyjKategorieWgId, uzyjPlatnosci, uzyjPozycjeWgId, uzyjWidokowPlatnosci } from '../dane/widoki.js'
import { ListaPlatnosci } from '../ui/ListaPlatnosci.js'
import { SzkieletKalendarza, SzkieletListy } from '../ui/Szkielet.js'
import { WyborMiesiaca, useMiesiac } from '../ui/WyborMiesiaca.js'
import { zl } from '../ui/format.js'
import { DNI_SKROT, MIESIACE, dzienTygodnia, pelnaData } from '../ui/format.js'
import { FormularzPozycji } from './FormularzPozycji.js'
import './kalendarz.css'

/**
 * Buduje siatkę tygodni obejmującą cały miesiąc, dosypując dni sąsiednich
 * miesięcy. Tydzień zaczyna się w poniedziałek, zgodnie z polskim zwyczajem.
 */
export function siatkaMiesiaca(rok: number, miesiac: number): IsoDate[] {
  const pierwszy = toIso(rok, miesiac, 1)
  const przesuniecie = dzienTygodnia(pierwszy)
  const start = addDays(pierwszy, -przesuniecie)
  const potrzebne = przesuniecie + daysInMonth(rok, miesiac)
  const tygodnie = Math.ceil(potrzebne / 7)
  return Array.from({ length: tygodnie * 7 }, (_, indeks) => addDays(start, indeks))
}

/** Kompaktowa siatka jednego miesiąca — kafelek widoku rocznego. */
function MiniMiesiac({
  rok,
  miesiac,
  dniZPlatnosciami,
  suma,
  onWybierz,
}: {
  rok: number
  miesiac: number
  dniZPlatnosciami: ReadonlySet<string>
  suma: number
  onWybierz(): void
}) {
  const dni = siatkaMiesiaca(rok, miesiac)
  const prefiks = `${rok}-${String(miesiac).padStart(2, '0')}`

  return (
    <button type="button" className="mini karta" onClick={onWybierz}>
      <span className="mini__naglowek">
        <span className="mini__nazwa">{MIESIACE[miesiac - 1]}</span>
        <span className="mini__suma">{suma === 0 ? '—' : zl(suma)}</span>
      </span>
      <span className="mini__siatka" aria-hidden="true">
        {dni.map((dzien) => (
          <span
            key={dzien}
            className={[
              'mini__dzien',
              dzien.startsWith(prefiks) ? '' : 'mini__dzien--obcy',
              dniZPlatnosciami.has(dzien) ? 'mini__dzien--platnosc' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          />
        ))}
      </span>
    </button>
  )
}

function WidokRoczny({
  rok,
  onWybierzMiesiac,
}: {
  rok: number
  onWybierzMiesiac(miesiac: number): void
}) {
  const okres = yearPeriod(rok)
  const platnosci = uzyjPlatnosci(okres.from, okres.to)
  const widoki = uzyjWidokowPlatnosci(okres.from, okres.to)

  const dniZPlatnosciami = useMemo(
    () => new Set((platnosci ?? []).map((platnosc) => platnosc.dueDate)),
    [platnosci],
  )

  const sumyMiesiecy = useMemo(() => {
    const sumy = new Array<number>(12).fill(0)
    for (const widok of widoki ?? []) {
      if (widok.type !== 'expense' || widok.status === 'skipped') continue
      const indeks = Number(widok.dueDate.slice(5, 7)) - 1
      sumy[indeks] = (sumy[indeks] ?? 0) + widok.amountPlnMinor
    }
    return sumy
  }, [widoki])

  return (
    <div className="rok wjazd-listy">
      {Array.from({ length: 12 }, (_, indeks) => (
        <MiniMiesiac
          key={indeks}
          rok={rok}
          miesiac={indeks + 1}
          dniZPlatnosciami={dniZPlatnosciami}
          suma={sumyMiesiecy[indeks] ?? 0}
          onWybierz={() => onWybierzMiesiac(indeks + 1)}
        />
      ))}
    </div>
  )
}

export function Kalendarz() {
  const [zakres, ustawZakres] = useState<'miesiac' | 'rok'>('miesiac')
  const { miesiac, przesun, doDzis, ustaw: ustawMiesiac, kierunek, odslona } = useMiesiac()
  const okres = monthPeriod(miesiac.rok, miesiac.miesiac)
  const platnosci = uzyjPlatnosci(okres.from, okres.to)
  const pozycje = uzyjPozycjeWgId()
  const kategorie = uzyjKategorieWgId()
  const przelacz = useOdhaczanie()
  const [nowaData, ustawNowaDate] = useState<IsoDate | null>(null)

  const dzis = todayIso()
  const dni = useMemo(() => siatkaMiesiaca(miesiac.rok, miesiac.miesiac), [miesiac])

  const wgDnia = useMemo(() => {
    const mapa = new Map<IsoDate, Platnosc[]>()
    for (const platnosc of platnosci ?? []) {
      const lista = mapa.get(platnosc.dueDate)
      if (lista === undefined) mapa.set(platnosc.dueDate, [platnosc])
      else lista.push(platnosc)
    }
    return mapa
  }, [platnosci])

  const prefiks = `${miesiac.rok}-${String(miesiac.miesiac).padStart(2, '0')}`

  return (
    <>
      <header className="naglowek-ekranu">
        <div>
          <h1>Kalendarz</h1>
          <p>{zakres === 'rok' ? 'Kliknij miesiąc, żeby go otworzyć' : 'Kliknij dzień, żeby dopisać wydatek'}</p>
        </div>
        {zakres === 'miesiac' ? (
          <WyborMiesiaca miesiac={miesiac} onPrzesun={przesun} onDzis={doDzis} />
        ) : (
          <WyborMiesiaca
            miesiac={miesiac}
            onPrzesun={(o) => przesun(o * 12)}
            onDzis={doDzis}
            tylkoRok
          />
        )}
      </header>

      <div className="przelaczniki kalendarz__zakres" role="group" aria-label="Zakres kalendarza">
        {(
          [
            ['miesiac', 'Miesiąc'],
            ['rok', 'Rok'],
          ] as const
        ).map(([wartosc, etykieta]) => (
          <button
            key={wartosc}
            type="button"
            className={zakres === wartosc ? 'pigulka pigulka--wybrana' : 'pigulka'}
            aria-pressed={zakres === wartosc}
            onClick={() => ustawZakres(wartosc)}
          >
            {etykieta}
          </button>
        ))}
      </div>

      {zakres === 'rok' ? (
        <WidokRoczny
          rok={miesiac.rok}
          onWybierzMiesiac={(numer) => {
            ustawMiesiac({ rok: miesiac.rok, miesiac: numer })
            ustawZakres('miesiac')
          }}
        />
      ) : platnosci === undefined ? (
        <SzkieletKalendarza />
      ) : (
      <div
        className="kalendarz"
        role="grid"
        aria-label={`Kalendarz ${prefiks}`}
        key={odslona}
        data-kierunek={kierunek}
      >
        <div className="kalendarz__naglowki" role="row">
          {DNI_SKROT.map((dzien) => (
            <span key={dzien} role="columnheader" className="kalendarz__naglowek">
              {dzien}
            </span>
          ))}
        </div>

        <div className="kalendarz__siatka wjazd-listy">
          {dni.map((dzien) => {
            const wTymMiesiacu = dzien.startsWith(prefiks)
            const lista = wgDnia.get(dzien) ?? []
            const doZaplaty = lista.filter((platnosc) => platnosc.status === 'planned').length
            return (
              <button
                key={dzien}
                type="button"
                role="gridcell"
                className={[
                  'kalendarz__dzien',
                  wTymMiesiacu ? '' : 'kalendarz__dzien--obcy',
                  dzien === dzis ? 'kalendarz__dzien--dzis' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => ustawNowaDate(dzien)}
                aria-label={`${pelnaData(dzien)}${lista.length === 0 ? ', brak płatności' : `, płatności: ${lista.length}`}`}
              >
                <span className="kalendarz__numer">{Number(dzien.slice(8))}</span>
                {lista.length > 0 ? (
                  <span className="kalendarz__kropki" aria-hidden="true">
                    {lista.slice(0, 4).map((platnosc) => {
                      const pozycja = pozycje?.get(platnosc.itemId)
                      const kategoria =
                        pozycja?.categoryId == null ? undefined : kategorie?.get(pozycja.categoryId)
                      return (
                        <span
                          key={platnosc.id}
                          className="kropka-kategorii"
                          style={{
                            background: kategoria?.color ?? 'var(--tekst-3)',
                            opacity: platnosc.status === 'planned' ? 1 : 0.4,
                          }}
                        />
                      )
                    })}
                  </span>
                ) : null}
                {doZaplaty > 4 ? <span className="kalendarz__wiecej">+{doZaplaty - 4}</span> : null}
              </button>
            )
          })}
        </div>
      </div>
      )}

      {zakres === 'rok' ? null : (
      <section aria-labelledby="platnosci-miesiaca-kalendarz">
        <h2 className="naglowek-sekcji" id="platnosci-miesiaca-kalendarz">
          Wszystkie płatności miesiąca
        </h2>
        {platnosci === undefined ? (
          <SzkieletListy ile={3} />
        ) : (
        <ListaPlatnosci
          platnosci={platnosci}
          pozycje={pozycje ?? new Map()}
          kategorie={kategorie ?? new Map()}
          onPrzelacz={(platnosc) => void przelacz(platnosc)}
          pustaTresc="Ten miesiąc jest pusty."
        />
        )}
      </section>
      )}

      {nowaData !== null ? (
        <FormularzPozycji
          domyslnaData={nowaData}
          domyslnyRytm="one_off"
          onZamknij={() => ustawNowaDate(null)}
        />
      ) : null}
    </>
  )
}
